import {
  Component,
  Input,
  Output,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  OnDestroy,
  ChangeDetectorRef,
  EventEmitter,
  signal,
  computed,
  ChangeDetectionStrategy
} from '@angular/core';

import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DemandeRecrutement, IntitulePoste } from '@models/domains/recruitment';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { takeUntil, Subject, startWith } from 'rxjs';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { CustomLuxonDateAdapter } from 'src/app/shared/utils/custom-luxon-date-adapter';
import { MY_FORMATS } from 'src/app/shared/utils/date-pickr-custom-luxon-format';
import { DemandeStatusService } from 'src/app/core/services/demande-status.service';
import { DemandeRecrutementService } from 'src/app/core/services/demande-recrutement.service';
import { ValidationDetail } from '@models/shared';
import { CurrentValidationTableComponent } from '../../validation-tables/current-validation-table/current-validation-table.component';
import { ValidationHistoryTableComponent } from '../../validation-tables/validation-history-table/validation-history-table.component';
import type { PdfViewerAction } from '../pdf-viewer.types';
import { FilesAttachmentComponent } from '../../files-attachment/files-attachment.component';
import { FileAttachment } from '@models/shared';
import { FileUtilsService } from 'src/app/core/services/file-utils.service';
import { Poste } from '@models/domains/posts';
import { 
  DestinatairesFormValue, 
  CaracteristiquesFormValue,
  DemandeRecrutementInformationsValue,
  DemandeRecrutementFormValue,
  DemandeRecrutementFormControls
} from '@models/api/requests';
import { DestinatairesFormComponent } from '../../components/destinataires-form/destinataires-form.component';
import { InformationsFormComponent } from '../../components/informations-form/informations-form.component';
import { CaracteristiquesFormComponent } from '../../components/caracteristiques-form/caracteristiques-form.component';
import { noWhitespaceValidator } from 'src/app/shared/utils/no-whitespace-validator';

@Component({
    selector: 'app-demande-recrutement-form',
    imports: [
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatTooltipModule,
    CurrentValidationTableComponent,
    ValidationHistoryTableComponent,
    FilesAttachmentComponent,
    DestinatairesFormComponent,
    InformationsFormComponent,
    CaracteristiquesFormComponent
],
    templateUrl: './demande-recrutement-form.component.html',
    styleUrls: ['./demande-recrutement-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
        { provide: DateAdapter, useClass: CustomLuxonDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }
    ]
})
export class DemandeRecrutementFormComponent implements OnInit, OnDestroy {
  readonly nbreCaratereTexte: string = "Votre texte ne doit pas dépasser 720 caractères."
  
  // Signal-based state for inputs
  private readonly _demandeDetails = signal<DemandeRecrutement | null>(null);
  private readonly _readOnly = signal<boolean>(true);
  private readonly _viewMode = signal<PdfViewerAction>('view');
  
  @Input() set demandeDetails(value: DemandeRecrutement | null) {
    this._demandeDetails.set(value);
    if (value) {
      this.loadValidationSteps();
      this.loadPj();
      this.populateFormsWithDetails();
    }
  }
  get demandeDetails() {
    return this._demandeDetails();
  }
  
  @Input() set readOnly(value: boolean) {
    this._readOnly.set(value);
    if (this.recrutementForm) {
      // Handle side effect directly in setter
      this.updateFormsDisabledState(value);
    }
  }
  get readOnly() {
    return this._readOnly();
  }
  
  @Input() set viewMode(value: PdfViewerAction) {
    this._viewMode.set(value);
  }
  get viewMode() {
    return this._viewMode();
  }
  
  // Computed signals for derived state
  readonly advice = computed(() => {
    const demande = this._demandeDetails();
    // Provide a stable null value during initialization to avoid timing issues
    if (!demande) return null;
    
    return (demande?.destinataireAvis && demande?.demandeurAvis)
      ? { 
          emetteur: { nom: demande.demandeurAvis.nom ?? '', prenom: demande.demandeurAvis.prenom ?? '' },
          destinataire: { nom: demande.destinataireAvis.nom ?? '', prenom: demande.destinataireAvis.prenom ?? '' },
          note: demande.demandeAvisMotif ?? ''
        }
      : null;
  });
  
  readonly PJreadOnly = computed(() => this._readOnly());
  @Input() set motifRefus(value: string) {
    this.motifRefusControl.setValue(value);
  }
  @ViewChildren(MatExpansionPanel) allpanels!: QueryList<MatExpansionPanel>;

  // ViewChild references for testing business logic (not used in production code)
  @ViewChild(InformationsFormComponent) informationsFormComponent!: InformationsFormComponent;
  @ViewChild(CaracteristiquesFormComponent) caracteristiquesFormComponent!: CaracteristiquesFormComponent;
  @ViewChild(DestinatairesFormComponent) destinatairesFormComponent!: DestinatairesFormComponent;

  // Forms
  recrutementForm: FormGroup<DemandeRecrutementFormControls>;
  motifRefusControl: FormControl<string> = new FormControl<string>('', { 
    nonNullable: true, 
    validators: [noWhitespaceValidator] 
  });

  // State for child components
  selectedPosteId: string | null = null;
  currentTypeRecrutement: any = null;
  private readonly _selectedDirection = signal<DemandeRecrutementInformationsValue['direction'] | null>(null);
  private readonly _selectedService = signal<DemandeRecrutementInformationsValue['service'] | null>(null);
  readonly selectedDirection = computed(() => this._selectedDirection());
  readonly selectedService = computed(() => this._selectedService());

  validationSteps: ValidationDetail[] = [];
  attachments: FileAttachment[] = [];
  showAttachments = false;

  // File attachment state
  @Output() filesSelected = new EventEmitter<File[]>();
  selectedFileNames: File[] = [];
  invalidFile: boolean = false;
  isDragging: boolean = false;
  PJTotal: number = 0;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly demandeRecrutementService: DemandeRecrutementService,
    public readonly demandeStatusService: DemandeStatusService,
    private readonly cdr: ChangeDetectorRef,
    private readonly fileUtils: FileUtilsService
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    this.recrutementForm
      .get('informationsData')
      ?.valueChanges.pipe(startWith(this.recrutementForm.get('informationsData')?.value), takeUntil(this.destroy$))
      .subscribe((value) => {
        const informations = value ?? null;
        this.updateCaracteristiquesState(informations);
        this.updateDestinatairesStructure(informations);
      });
  }


  initializeForms(): void {
    this.recrutementForm = this.fb.group<DemandeRecrutementFormControls>({
      informationsData: this.fb.control<DemandeRecrutementInformationsValue | null>(null, Validators.required),
      caracteristiquesData: this.fb.control<CaracteristiquesFormValue | null>(null, Validators.required),
      motivationDemande: this.fb.control<string>({ value: '', disabled: this._readOnly() }, { 
        nonNullable: true, 
        validators: [Validators.required, noWhitespaceValidator] 
      }),
      destinatairesData: this.fb.control<DestinatairesFormValue | null>(null, Validators.required)
    });

    // Update disabled state based on readOnly
    this.updateFormsDisabledState(this._readOnly());
  }

  updateFormsDisabledState(disabled: boolean): void {
    if (disabled) {
      // Read-only mode: disable everything
      this.recrutementForm.disable({ emitEvent: false });
    } else {
      this.recrutementForm.enable({ emitEvent: false });
    }
  }

  // Form population
  populateFormsWithDetails(): void {
    const demande = this._demandeDetails();
    if (!demande) return;
    const formData: DemandeRecrutementFormValue = {
      informationsData: {
        typeRecrutement: demande.typeRecrutement ?? null,
        pole: demande.pole ?? null,
        direction: demande.direction ?? null,
        service: demande.serviceFonctionnel ?? null,
        numPoste: demande.intitulePoste ?? null,
        intitulePoste: demande.intitulePoste ?? null,
        motifLiberation: demande.motifLiberationPoste ?? null
      },
      caracteristiquesData: {
        dateRecrutement: demande.dateRecrSouhaite ?? null,
        dateDepart: demande.dateDepartDefinitif ?? null,
        interne:
          demande.typeRecrutementSouhaite === 'Interne' || demande.typeRecrutementSouhaite === 'Interne_Externe',
        externe:
          demande.typeRecrutementSouhaite === 'Externe' || demande.typeRecrutementSouhaite === 'Interne_Externe',
        etatPoste: demande.etatPoste ?? null,
        occupationPoste: demande.occupationPoste ?? null,
        detenteurPoste: this.getDetenteurValue(),
        categoriePoste: demande.categoriePoste ?? null,
        posteBudgete: demande.posteBudgete ?? null
      },
      motivationDemande: demande.motivationDemande || '',
      destinatairesData: {
        destinataires: demande.destinataires || [],
        destinataireValidation: demande.destinataireValidation ?? null,
        destinataireChargeGpec: demande.destinataireCharge ?? null,
        destinataireDrh: demande.destinataireDRH ?? null,
        destinataireDg: demande.destinataireDG ?? null,
        destinataireResponsable: demande.destinataireResponsable ?? null,
        destinataireDgs: demande.destinataireDgs ?? null,
        destinataireArchivageRh: demande.destinataireArchivage ?? null,
        directionDemande: demande.direction ?? null
      }
    };

    // Patch all values at once with type safety - no events during initialization
    this.recrutementForm.patchValue(formData, { emitEvent: false });

    // Set selectedPosteId to trigger child component's poste data loading
    this.selectedPosteId = demande.intitulePoste?.id || null;
    this.updateDestinatairesStructure(formData.informationsData);
    
    // Mark form as touched and update validity
    this.recrutementForm.markAsTouched();
    this.recrutementForm.updateValueAndValidity({ emitEvent: false });
  }

  getSafeStatusText(): string {
    return this.demandeStatusService.getStatusText(this._demandeDetails()?.statut);
  }

  getSafeStatusClass(): string {
    return this.demandeStatusService.getStatusClass(this._demandeDetails()?.statut);
  }

  // Type-safe form value access methods
  getFormValue(): DemandeRecrutementFormValue | null {
    return this.recrutementForm.valid ? this.recrutementForm.value as DemandeRecrutementFormValue : null;
  }

  getRawFormValue(): DemandeRecrutementFormValue {
    return this.recrutementForm.getRawValue();
  }

  isFormValid(): boolean {
    return this.recrutementForm.valid;
  }

  getFormErrors(): { [key: string]: any } | null {
    return this.recrutementForm.errors;
  }

  markAllFieldsAsTouched(): void {
    this.recrutementForm.markAllAsTouched();
  }

  public getInvalidControls(): string[] {
    let invalidControls: string[] = [];

    // Check child components (available for testing)
    if (this.informationsFormComponent) {
      invalidControls = invalidControls.concat(this.informationsFormComponent.getInvalidControls());
    }
    if (this.caracteristiquesFormComponent) {
      invalidControls = invalidControls.concat(this.caracteristiquesFormComponent.getInvalidControls());
    }
    if (this.destinatairesFormComponent) {
      invalidControls = invalidControls.concat(this.destinatairesFormComponent.getInvalidControls());
    }

    // Check native controls
    const motivationControl = this.recrutementForm.get('motivationDemande');
    if (motivationControl && motivationControl.invalid) {
      invalidControls.push('motivationDemande');
    }

    return invalidControls;
  }

  loadValidationSteps(): void {
    if (this._demandeDetails()?.id) {
      this.demandeRecrutementService
        .getDetailsValidations(this._demandeDetails()!.id!, 'Recrutement')
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (steps) => {
            this.validationSteps = steps;
            console.log('validation history (recrutement)', steps);
          },
          error: (_error) => { }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPosteSelected(poste: Poste | IntitulePoste | null): void {
    this.selectedPosteId = poste?.id || null;
  }

  // pièces jointes ----------------------------------------------
  loadPj(): void {
    const demandeId = this.demandeDetails?.id;
    if (!demandeId) return;
    
    this.demandeRecrutementService.getPj(demandeId).subscribe({
      next: (files: FileAttachment[]) => {
        this.attachments = files.map((pj: FileAttachment) => ({
          id: pj.id,
          originalFileName: pj.originalFileName,
          fileName: pj.fileName,
          blobattachment: this.fileUtils.base64ToBlob(pj.content),
          content: pj.content,
          demande: pj.demande
        }));
        this.showAttachments = files.length > 0;
        this.PJTotal = files.length;
        this.cdr.markForCheck();
      },
      error: (error: unknown) => {
        console.error('Error loading attachments:', error);
      }
    });
  }

  closeAttachments(): void {
    this.showAttachments = false;
  }

  removePJ(event: FileAttachment[]): void {
    const fileToDelete = event[0];
    const demandeId = this._demandeDetails()?.id;
    
    if (!demandeId || !fileToDelete.id) return;
    
    this.demandeRecrutementService
      .deletePj(demandeId, fileToDelete.id.toString())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.PJTotal -= 1;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Erreur de suppression de pièce jointe:', error);
        }
      });
  }

  triggerFileInput(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  onFileSelected(event: Event | FileList): void {
    const inputEl = (event as Event & { target: HTMLInputElement })?.target;
    const fileSelected = inputEl?.files || event as FileList;

    try {
      if (!fileSelected) return;
      
      const files = Array.from(fileSelected) as File[];
      const pdfFiles = files.filter((file) => file.type === 'application/pdf');

      if (pdfFiles.length !== files.length) {
        this.invalidFile = true;
        return;
      }
      
      this.invalidFile = false;
      this.selectedFileNames = [...this.selectedFileNames, ...pdfFiles];

      const demandeId = this._demandeDetails()?.id;
      if (!demandeId) return;
      
      this.demandeRecrutementService
        .postPj(demandeId, this.selectedFileNames)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => this.loadPj(),
          error: (error) => {
            console.error("Erreur de l'ajout de pièce jointe:", error);
          }
        });
    } finally {
      if (inputEl) {
        // Reset input so selecting the same file again triggers a change event.
        inputEl.value = '';
      }
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  handleDragDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (!files) return;
    
    const droppedFiles = Array.from(files) as File[];
    const pdfFiles = droppedFiles.filter((file) => file.type === 'application/pdf');

    if (pdfFiles.length) {
      this.invalidFile = false;
      const fileList = this.createFileList(pdfFiles);
      this.onFileSelected(fileList);
    } else {
      this.invalidFile = false;
    }
  }

  private getDetenteurValue(): string | null {
    const demande = this._demandeDetails();
    if (!demande) return null;
    
    if (demande.detenteurNom) {
      return demande.detenteurNom;
    }
    
    if (typeof demande.detenteurPoste === 'string') {
      return demande.detenteurPoste;
    }
    
    // Extract name from PersonalUser object when detenteurNom is null
    if (demande.detenteurPoste && typeof demande.detenteurPoste === 'object') {
      const user = demande.detenteurPoste;
      return [user.nom, user.prenom].filter(Boolean).join(' ') || null;
    }
    
    return null;
  }

  private createFileList(files: File[]): FileList {
    const dataTransfer = new DataTransfer();
    files.forEach(file => dataTransfer.items.add(file));
    return dataTransfer.files;
  }

  private updateCaracteristiquesState(informations: DemandeRecrutementInformationsValue | null): void {
    const caracteristiquesControl = this.recrutementForm.get('caracteristiquesData');
    if (!caracteristiquesControl) return;

    const typeRecrutement = informations?.typeRecrutement;
    this.currentTypeRecrutement = typeRecrutement;

    if (this._readOnly()) {
      caracteristiquesControl.disable({ emitEvent: false });
      return;
    }

    caracteristiquesControl.enable({ emitEvent: false });
    caracteristiquesControl.updateValueAndValidity({ emitEvent: false });
    this.recrutementForm.updateValueAndValidity({ emitEvent: false });
  }

  private updateDestinatairesStructure(informations: DemandeRecrutementInformationsValue | null): void {
    const nextDirection = informations?.direction ?? null;
    const nextService = informations?.service ?? null;

    const currentDirectionId = this.extractId(this._selectedDirection());
    const nextDirectionId = this.extractId(nextDirection);
    if (currentDirectionId !== nextDirectionId) {
      this._selectedDirection.set(nextDirection ?? null);
    }

    const currentServiceId = this.extractId(this._selectedService());
    const nextServiceId = this.extractId(nextService);
    if (currentServiceId !== nextServiceId) {
      this._selectedService.set(nextService ?? null);
    }

    this.cdr.markForCheck();
  }

  private extractId(entity: any): string | null {
    if (!entity) return null;
    if (typeof entity === 'string') return entity;
    if (typeof entity === 'number') return entity.toString();
    if (entity && typeof entity === 'object' && 'id' in entity) {
      return (entity as { id: string | number | null | undefined }).id?.toString() ?? null;
    }
    return null;
  }
}
